/* eslint-disable no-underscore-dangle */

'use-strict';

import { IScope, ITimeoutService, ILocationService } from 'angular';
import objectHash from 'object-hash';
import { SimpleChanges } from '@angular/core';

import { CommentAPIService } from '../../shared/api/comment-api.service';
import { SalesAPIService } from '../../shared/api/sales-api.service';
import { FileService } from '../../shared/storage/file.service';
import { InterfaceService } from '../../shared/interface/interface.service';
import { ToastService, ToastType } from '../../ui-components/toast/toast.service';
import { VehicleLockRequest } from '../../src/app/@shared-types/vehicle/lock';
import { UsersService } from '../../shared/users/users.service';
import { AutoboardVehicle } from '../../src/app/@shared-types/vehicle';
import { Comment, ECommentContext, ECommentType } from '../../src/app/@shared-types/misc/comment';
import { EFileSection, File } from '../../src/app/@shared-types/misc/file';
import LogService from '../../src/app/shared/services/log';
import { NumberConversionService } from '../../shared/converter/number-converter.service';
import { SocketService } from '../../shared/connections/web-socket.service';
import { OpportunityModel } from '../../src/app/shared/models/opportunity/opportunity';
import { EnrichedOpportunity } from '../../src/app/@shared-types/opportunites';
import { PermissionsService } from '../authorization/permissions.service';
import { UserRole } from '../../src/app/@types/user';
import { EFuelType } from '../../src/app/@shared-types/vehicle/enums';

export interface VehicleDetailPrices {
  main?: string,
  mainDescription?: string,
  secondary?: string,
  secondaryDescription?: string,
}

export interface APIWarning {
  api: string,
  warnings: string[],
}

/* @ngInject */
export default class VehicleDetailController {
  private lockPreloader = false;

  private ready = false;

  private vehicle!: AutoboardVehicle;

  private hash!: string;

  private exit!: () => void;

  private dummyVehicle!: boolean;

  private active = false;

  private $rootScope: IScope;

  private $scope: IScope;

  private salesApiService: SalesAPIService;

  private commentApiService: CommentAPIService;

  private fileService: FileService;

  private interfaceService: InterfaceService;

  private numberConversionService: NumberConversionService;

  private logService: LogService;

  private toastService: ToastService;

  private usersService: UsersService;

  private socketService: SocketService;

  private opportunityModel: OpportunityModel;

  private $location: ILocationService;

  private permissionsService: PermissionsService;

  private overlay: {
    visiblity: boolean,
    action: string,
    title: string,
    customerId?: string,
  } = {
    visiblity: false,
    action: '',
    title: '',
  };

  public generalComments: Comment[] | null = [];

  public fundingComments: Comment[] | null = [];

  public files: File[] | null = null;

  private prices: VehicleDetailPrices = {};

  private lockOtherClient = false;

  private vehicleImages: string[] = [];

  /** When changed, the gallery will re-initialize */
  public galleryHash = '';

  /** Used to redraw one time bindings. Will be set false when updates should be processed. */
  public refresher = true;

  private $timeout: ITimeoutService;

  public warnings: APIWarning[] = [];

  private delayedBindingUpdate = false;

  private opportunities: EnrichedOpportunity[] = [];

  constructor(
    $rootScope: IScope,
    $scope: IScope,
    salesApiService: SalesAPIService,
    commentApiService: CommentAPIService,
    fileService: FileService,
    interfaceService: InterfaceService,
    numberConversionService: NumberConversionService,
    toastService: ToastService,
    usersService: UsersService,
    logService: LogService,
    $timeout: ITimeoutService,
    socketService: SocketService,
    opportunityModel: OpportunityModel,
    $location: ILocationService,
    permissionsService: PermissionsService,
  ) {
    this.$rootScope = $rootScope;
    this.$scope = $scope;
    this.salesApiService = salesApiService;
    this.commentApiService = commentApiService;
    this.fileService = fileService;
    this.interfaceService = interfaceService;
    this.numberConversionService = numberConversionService;
    this.toastService = toastService;
    this.usersService = usersService;
    this.logService = logService;
    this.$timeout = $timeout;
    this.socketService = socketService;
    this.opportunityModel = opportunityModel;
    this.$location = $location;
    this.permissionsService = permissionsService;

    this.refresher = true;
  }

  $onInit(): void {}

  $doCheck(): void {
    if (this.vehicle && !this.active) {
      this.active = true;
      this.init();
    }
  }

  /** One time bindings are updated, when the provided hash
   * value changes. The global `refresher` will be set false
   * for a very short time and then be set to true again.
   *
   * Prevent user editing a vehicle from seeing updates.
   * */
  $onChanges(changesObj: SimpleChanges): void {
    if (changesObj.hash && this.overlay.action) {
      this.delayedBindingUpdate = true;
    } else if (changesObj.hash && !this.overlay.action) {
      this.updateOneTimeBindings();
    }
  }

  init():void {
    this.$rootScope.$on('updateComments', () => {
      this.getDetailedCarData();
    });

    (this.$rootScope as any).bodyOverFlowHide = true;
    this.getDetailedCarData();
    this.initWarnings();

    setTimeout(() => {
      this.ready = true;
      this.interfaceService.applyScope(this.$scope);
    }, 10);
  }

  updateOneTimeBindings(): void {
    this.refresher = false;

    this.getDetailedCarData();
    this.initWarnings();

    this.$timeout(() => {
      this.refresher = true;
      this.interfaceService.applyScope(this.$scope);
    });
  }

  /**
   * When directive initializes some additonal data must be retrieved for display.
   */
  async getDetailedCarData(): Promise<void> {
    this.lockOtherClient = false;
    this.vehicleImages = this.vehicle.vehicleData.images || [];

    if (
      this.vehicle.vehicleData.lock
      && this.vehicle.vehicleData.lock.user !== this.usersService.currentUserId()
    ) {
      this.lockOtherClient = true;
    } else if (this.vehicle.vehicleData.lock) {
      this.toolbarAction(this.vehicle.vehicleData.lock.reason, false, '');
    } else if (this.$location.search().action) {
      const { customerId, action } = this.$location.search();

      this.toolbarAction(action, true, '', customerId);
      this.$location.search('action', null);
      this.$location.search('customerId', null);
    }

    this.galleryHash = '';
    this.$timeout(() => {
      this.galleryHash = objectHash(this.vehicle.vehicleData.images || {});
    });

    this.fileService.getByContext(EFileSection.VEHICLE, String(this.vehicle._id))
      .then((files) => {
        this.files = files;
        return this.commentApiService.getByContext(
          ECommentContext.VEHICLE,
          String(this.vehicle._id),
        );
      })
      .then((comments) => {
        this.generalComments = comments.filter((c) => c.type !== ECommentType.FUNDING);
        this.fundingComments = comments.filter((c) => c.type === ECommentType.FUNDING);
      })
      .catch((e) => {
        this.toastService.toast(ToastType.ERROR, 'Fehler beim Laden der Detailansicht');
        this.logService.error(e);
      });

    if (this.permissionsService.isPermitted([UserRole.SELLER, UserRole.SELLER_PLUS])) {
      this.opportunities = await this.opportunityModel.getForVehicle(String(this.vehicle._id));
    }

    const vatRate = this.vehicle.vehicleData.price.vatRate || 0.19;
    const prices = this.vehicle.vehicleData.price;

    if (prices.retailPrices.offer) {
      this.prices.main = this.numberConversionService
        .calculatePriceWithTax(prices.retailPrices.offer, vatRate);
      this.prices.mainDescription = `Aktuelle UPE (inkl. ${vatRate * 100}% MwSt.)`;
    } else if (prices.retailPrices.invoice) {
      this.prices.main = this.numberConversionService
        .calculatePriceWithTax(prices.retailPrices.invoice, vatRate);
      this.prices.mainDescription = `UPE Eingangsrechnung (inkl. ${vatRate * 100}% MwSt.)`;
    } else if (prices.retailPrices.manufacturer) {
      this.prices.main = this.numberConversionService
        .calculatePriceWithTax(prices.retailPrices.manufacturer, vatRate);
      this.prices.mainDescription = `UPE Hersteller (inkl. ${vatRate * 100}% MwSt.)`;
    }

    if (prices.campaignPrice) {
      this.prices.secondary = String(prices.campaignPrice);
      this.prices.secondaryDescription = prices.includesVat ? `Verkaufspreis (inkl. ${vatRate * 100}% MwSt.)` : 'Verkaufspreis (Differenzbesteuert nach §25a UStG)';
    } else if (prices.onlinePrice) {
      this.prices.secondary = String(prices.onlinePrice);
      this.prices.secondaryDescription = prices.includesVat ? `Preis Onlinebörsen (inkl. ${vatRate * 100}% MwSt.)` : 'Preis Onlinebörsen (Differenzbesteuert nach §25a UStG)';
    }

    this.interfaceService.applyScope(this.$scope);
  }

  /**
   * User selects action from toolbar.
   *
   * @param {SALES_TABLE_ACTIONS} action which form the user requested
   * @param {Boolean} lock if car needs to be locked before editing
   */
  async toolbarAction(
    action: string,
    lock: boolean,
    title: string,
    customerId?: string,
  ): Promise<void> {
    this.overlay.visiblity = true;
    this.overlay.action = action;
    this.overlay.title = title;
    this.overlay.customerId = customerId;

    if (lock && !this.dummyVehicle) {
      this.lockPreloader = true;
      const lockRequest: VehicleLockRequest = {
        reason: action,
        user: this.usersService.currentUserId(),
        vehicleId: String(this.vehicle._id),
      };

      this.salesApiService.postVehicleLock(lockRequest)
        .then(() => {
          this.lockPreloader = false;
          this.interfaceService.applyScope(this.$scope);
        })
        .catch((e) => {
          this.overlay.visiblity = false;
          this.overlay.action = '';
          delete this.overlay.customerId;
          this.toastService.toast(ToastType.ERROR, 'Fahrzeug kann nicht bearbeitet werden.');
          this.logService.error(e);
        });
    }
  }

  initWarnings(): void {
    this.warnings = [];

    if (this.vehicle.apiData?.MOBILE_SELLER?.errors) {
      this.warnings.push({
        api: 'mobile.de',
        warnings: this.vehicle.apiData.MOBILE_SELLER.errors,
      });
    }
  }

  public isPHEV = (): boolean => {
    switch (this.vehicle.modelData?.engine.fuel.type) {
      case EFuelType.HybridDieselPlugin:
      case EFuelType.HybridPetrolPlugin:
        return true;

      default: return false;
    }
  };

  public getFuelUnit = (): string => {
    switch (this.vehicle.modelData?.engine.fuel.type) {
      case EFuelType.CNG:
      case EFuelType.Hydrogen:
        return 'kg/100 km';

      default: return 'l/100 km';
    }
  };

  /**
   * When a form directive wants to exit, it calls this method.
   * If successfull, load the expanded view again to update everything.
   */
  async finishFormTransmission(
    removeLock: boolean,
    redirect: string,
  ): Promise<void> {
    this.overlay.visiblity = false;
    this.overlay.action = '';
    delete this.overlay.customerId;

    if (removeLock && !this.dummyVehicle) { // form submission was aborted
      await this.salesApiService.deleteVehicleLock(String(this.vehicle._id));
    }

    /** If user comes from eg. an opportunity directly
     * to an action / form, we will redirect to the origin
     * if provided as a query param. */
    if (this.$location.search().redirect || redirect) {
      const r = redirect || this.$location.search().redirect;
      this.$location.search('id', null);
      this.$location.search('redirect', null);
      this.interfaceService.navigateToUrl(r);
      return;
    }

    if (this.delayedBindingUpdate) {
      this.updateOneTimeBindings();
      this.delayedBindingUpdate = false;
    }

    this.getDetailedCarData();
    /** Forces refresher wrapper to update one time bindings of vehicle detail component
     * to see the changes made by the user. */
    this.$rootScope.$broadcast('refresherUpdate');
  }

  close(): void {
    this.ready = false;

    setTimeout(() => {
      this.exit();
      this.interfaceService.applyScope(this.$scope);
    }, 250);
  }
}
